/**
 * Reusable "📌 Pin to brief" affordance. Drop on any surface panel and
 * pass enough context for the §7.25 Builder to draft a BLUF line from
 * the pinned item.
 *
 * CD R10 Q8 long-term: lives in the R9 session bar as a "📌 N pinned"
 * indicator. For now (Phase 9.7 v1) the button is inline next to the
 * panel it pins.
 */

import { useEffect, useState } from 'react';
import {
  pinForBrief, unpinForBrief, isPinnedForBrief, subscribeToBriefPins,
  type PinnedItem,
} from './pin-to-brief';

export function PinToBriefButton({
  item, compact = false,
}: {
  item: Omit<PinnedItem, 'pinnedAt'>;
  compact?: boolean;
}) {
  const [pinned, setPinned] = useState(() => isPinnedForBrief(item.id));
  useEffect(() => {
    return subscribeToBriefPins(() => setPinned(isPinnedForBrief(item.id)));
  }, [item.id]);
  return (
    <button
      type="button"
      onClick={() => pinned ? unpinForBrief(item.id) : pinForBrief(item)}
      title={pinned ? 'Pinned for the next Minister Briefing Pack — click to unpin' : 'Pin this for the next Minister Briefing Pack'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: compact ? '2px 7px' : '4px 10px',
        fontFamily: 'var(--bm-font-mono)', fontSize: compact ? 10.5 : 11.5, fontWeight: 600,
        color: pinned ? 'var(--bm-bg)' : 'var(--bm-text-muted)',
        background: pinned ? 'var(--bm-accent, #6ea3ff)' : 'transparent',
        border: `1px solid ${pinned ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-border)'}`,
        borderRadius: 4, cursor: 'pointer',
      }}
    >
      <span aria-hidden>📌</span>
      {pinned ? 'Pinned to brief' : 'Pin to brief'}
    </button>
  );
}
